import React, { useState } from 'react';
import {
  validateFieldContext,
  FieldContextValidationResult,
  FieldContextIssue,
} from '../../../utils/faqApi';
import { FullFieldContext } from './FieldEditor';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function scoreColor(score: number): string {
  if (score >= 0.85) return 'text-green-700 bg-green-50 border-green-200';
  if (score >= 0.65) return 'text-yellow-700 bg-yellow-50 border-yellow-200';
  if (score >= 0.40) return 'text-orange-700 bg-orange-50 border-orange-200';
  return 'text-red-700 bg-red-50 border-red-200';
}

function scoreBar(score: number): string {
  if (score >= 0.85) return 'bg-green-500';
  if (score >= 0.65) return 'bg-yellow-400';
  if (score >= 0.40) return 'bg-orange-400';
  return 'bg-red-500';
}

const severityBadge: Record<FieldContextIssue['severity'], string> = {
  error: 'bg-red-100 text-red-800 border border-red-200',
  warning: 'bg-amber-100 text-amber-800 border border-amber-200',
  info: 'bg-blue-100 text-blue-800 border border-blue-200',
};

const severityOrder: Record<FieldContextIssue['severity'], number> = {
  error: 0,
  warning: 1,
  info: 2,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
interface ValidationPanelProps {
  topic: string;
  editedConfig: FullFieldContext | null;
}

const ValidationPanel: React.FC<ValidationPanelProps> = ({ topic, editedConfig }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<FieldContextValidationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleValidate = async () => {
    if (!editedConfig) return;
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const response = await validateFieldContext(topic, editedConfig);
      if (response.success && response.validation) {
        setResult(response.validation);
      } else {
        setError(response.error || 'Validation returned no result.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const sortedIssues = result
    ? [...result.issues].sort(
        (a, b) => severityOrder[a.severity] - severityOrder[b.severity]
      )
    : [];

  return (
    <div>
      <button
        onClick={handleValidate}
        disabled={loading || !editedConfig}
        className="w-full px-4 py-2 bg-gov-blue text-white rounded hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium mb-4"
      >
        {loading ? 'Validating with LLM…' : 'Validate Config'}
      </button>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          {error}
        </div>
      )}

      {result && (
        <div className="space-y-4">
          {/* Score */}
          <div className={`p-3 rounded border ${scoreColor(result.overall_score)}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold capitalize">{result.quality_label}</span>
              <span className="font-mono font-bold text-lg">
                {Math.round(result.overall_score * 100)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all ${scoreBar(result.overall_score)}`}
                style={{ width: `${result.overall_score * 100}%` }}
              />
            </div>
            <p className="mt-2 text-xs leading-relaxed">{result.summary}</p>
          </div>

          {/* Issues */}
          {sortedIssues.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                Issues ({sortedIssues.length})
              </h4>
              <ul className="space-y-2">
                {sortedIssues.map((issue, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <span
                      className={`flex-shrink-0 text-xs font-medium px-1.5 py-0.5 rounded ${severityBadge[issue.severity]}`}
                    >
                      {issue.severity}
                    </span>
                    <span className="text-gray-700">
                      <span className="font-medium text-gray-900">{issue.field}:</span>{' '}
                      {issue.message}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Suggestions */}
          {result.suggestions.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                Suggestions
              </h4>
              <ul className="space-y-1">
                {result.suggestions.map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-blue-500 mt-0.5 flex-shrink-0">→</span>
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ValidationPanel;
